import React, { useEffect, useState, useRef } from 'react';
import { compose } from 'ramda';
import Grid from '@material-ui/core/Grid';
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import withCaseInfo from 'hoc/withCaseInfo';
import withMeta from 'hoc/withMeta';
import Store from 'store/store';
import Charts from 'components/Charts';
import Router from 'components/Router/Router';
import TypicalAssetAllocationReportChart from 'components/Charts/Report/TypicalAssetAllocation';
import {
  PastPerformanceAggregatesType,
  PortfolioSplitTypeChartData,
  PolicyChargeTypeChartData,
  PolicyRiskScoreNamed,
  TotalOngoingChargeTypeChartDataNamed,
  AssetAllocationTypeChartData,
  AggregateCostAndChargesPoliciesType,
  AssetAllocationEvalueType
} from 'store/modules/Charts/types';
import { Button } from 'components/Base';
import { findMaxCharge, errHelper } from 'utils';
import api from 'api';
import TypicalAssetAllocations from 'components/RiskAnalysis/TypicalAssetAllocation/TypicalAssetAllocation';
import { RiskGroupType } from 'store/modules/Firm/types';
import { Notify } from 'utils';
import { ExistingPolicyType } from 'store/modules/ExistingPolicies/types';
import { PolicyType } from 'store/modules/Policies/types';
import { generateImage } from 'utils/report';
import PastPerformance from 'components/IPS/PastPerformance';
import { getAllPerformanceData } from 'components/PastPerformance/utils';
import {
  combineCharges,
  combinePolicyRiskScores
} from 'components/Policies/utils';
import ScatterChart from 'components/InvestmentAnalyser/ScatterChart';
import {
  mapCombinedBreakdownData,
  mapSectorData
} from 'components/Charts/Utils/breakdowns';
import CountryBreakdownChart from 'components/InvestmentAnalyser/CountryBreakdownChart';
import { useDispatch } from 'react-redux';
import {
  fetchDataIfNeeded,
  getExistingPolicies,
  getPolicies,
  getRiskProfiles
} from 'components/Cases/utils';
import { adviserChargesIndex } from 'components/Settings/Palette/utils';
import axios from 'axios';
import MorningstarRiskToleranceReportChart from 'components/Charts/Report/MorningstarRiskToleranceReportChart';
import { getTargetAllocationData } from 'components/RiskAnalysis/Morningstar/targetAllocation';
import { getComfortRanges } from 'components/Charts/Utils/MorningstarRiskProfiler/utils';
import { comfortRangeConfig } from 'components/RiskMapping/utils';
import MorningstarRiskAnalysisReportChart from 'components/Charts/Report/MorningstarRiskAnalysisReportChart';

const { useParams, useHistory } = Router;
const { connect, selectors, actions } = Store;

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      flexGrow: 1,
      backgroundColor: '#fff',
      maxWidth: '1200px',
      minWidth: '1200px',
      position: 'absolute'
    },
    gridBox: {
      height: '350px',

      display: 'flex',
      flexDirection: 'column',
      padding: '1rem',
      border: '1px solid #e5dede'
    },
    riskDescription: {
      textAlign: 'center',
      verticalAlign: 'middle',
      color: '#fff'
    },

    chartBox: {
      height: '100%',
      padding: '2rem 0'
    },

    paper: {
      backgroundColor: '#fff',
      padding: '2rem 1rem',
      border: '1px solid #e5dede'
    },
    print: {
      backgroundColor: '#fff'
    },
    mask: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: 500000,
      backgroundColor: 'white',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    modal: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      width: '450px',
      height: '130px;'
    },
    btnContainer: {
      '& button': {
        margin: '0 1rem'
      }
    },
    legend: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      marginTop: '-10px'
    },
    legendSingle: {
      display: 'flex',
      alignItems: 'center',
      marginRight: '1rem'
    },
    legendBox: {
      width: '10px',
      minWidth: '10px',
      height: '10px',
      marginRight: '0.5rem',
      borderRadius: '50%'
    },
    smallBox: {
      width: '604px',
      maxWidth: '604px',
      maxHeight: '480px',
      height: '480px'
    },
    middleBox: {
      width: '800px',
      maxWidth: '800px',
      height: '400px',
      maxHeight: '400px'
    },
    semiMiddleBox: {
      width: '1000px',
      maxWidth: '1000px',
      height: '550px',
      maxHeight: '550px'
    },
    largeBox: {
      width: '1200px',
      maxWidth: '1200px',
      height: '550px',
      maxHeight: '550px'
    },
    scatterBox: {
      width: '1500px',
      maxWidth: '1500px',
      height: '300px',
      maxHeight: '300px'
    },
    tooltip: {
      margin: 0
    },
    tooltipCharge: {
      margin: 0,
      fontSize: '16px'
    }
  })
);

interface InvestmentPolicySummaryProps {
  pastPerformanceAggregate: PastPerformanceAggregatesType;
  portfolioSplit: Array<PortfolioSplitTypeChartData>;
  existingPoliciesCharges: Array<PolicyChargeTypeChartData>;
  portfolioRiskScore: Array<PolicyRiskScoreNamed>;
  riskSummaries: any;
  forecastGrossInvestmentReturn: any;
  recommendedPoliciesCharges: Array<PolicyChargeTypeChartData>;
  caseName: string;
  getDataForPdf: any;
  existingPoliciesRiskScore: any;
  recommendedPoliciesRiskScore: any;
  existingPolicyPastPerformance: any;
  existingPoliciesPerformance: any;
  totalPerformanceData: any;
  totalOngoinCharges: Array<TotalOngoingChargeTypeChartDataNamed>;
  assetAllocations: Array<AssetAllocationTypeChartData>;
  sectorBreakdown: any;
  regionalBreakdown: any;
  countryBreakdown: any;
  aggregateCostAndChargesPolicies: AggregateCostAndChargesPoliciesType[];
  hasExistingPolicies: boolean;
  firmRiskGroup: RiskGroupType;
  assetAllocationEvalue: AssetAllocationEvalueType[];
  existingPolicies: ExistingPolicyType[];
  recommendedPolicies: PolicyType[];
  location: any;
  setAggregateDataForPolicies: Function;
  setExistingPoliciesPerformance: Function;
  setPastPerformanceAggregates: Function;
  setPastPerformance: Function;
  setTotalOngoingCharges: Function;
  setTotalPerformanceData: Function;
  setAssetAllocationEvalue: Function;
  caseInfo: any;
  reportImages: any;
  recommendedRiskAnalysis: any;
}

const Printer = ({
  pastPerformanceAggregate,
  portfolioSplit,
  existingPoliciesCharges,
  recommendedPoliciesCharges,
  riskSummaries,
  forecastGrossInvestmentReturn,
  portfolioRiskScore,
  caseName,
  existingPoliciesRiskScore,
  recommendedPoliciesRiskScore,
  existingPolicyPastPerformance,
  totalOngoinCharges,
  assetAllocations,
  sectorBreakdown,
  regionalBreakdown,
  countryBreakdown,
  assetAllocationEvalue,
  aggregateCostAndChargesPolicies,
  hasExistingPolicies,
  firmRiskGroup,
  existingPolicies,
  recommendedPolicies,
  location,
  existingPoliciesPerformance,
  totalPerformanceData,
  setAggregateDataForPolicies,
  setExistingPoliciesPerformance,
  setPastPerformanceAggregates,
  setPastPerformance,
  setTotalOngoingCharges,
  setTotalPerformanceData,
  setAssetAllocationEvalue,
  caseInfo,
  reportImages,
  recommendedRiskAnalysis
}: InvestmentPolicySummaryProps) => {
  const theme = useTheme() as any;
  const dispatch = useDispatch();
  const isEvalue = caseInfo.riskProfiler === 'EValue';
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [pdf, setPdf] = useState('');
  const [isLoadingPdf, setIsLoadingPdf] = useState(true);
  const [reportFailed, setReportFailed] = useState(false);
  const [scatterChartDataExisting, setScatterChartDataExisting] = useState({});
  const [scatterChartDataRecommended, setScatterChartDataRecommended] =
    useState({});
  const [riskProfile, setRiskProfile] = useState(null as any);
  const classes = useStyles();
  const { portfolio } = useParams() as any;
  const history = useHistory();
  const aggregatePastPerformance = useRef(null);
  const existingPortfolioCharges = useRef(null);
  const recommendedPortfolioCharges = useRef(null);
  const forecastInvestmentReturn = useRef(null);
  const riskSummary = useRef(null);
  const aggregatePortfolioRisk = useRef(null);
  const recommendedPortfolioSplit = useRef(null);

  const totalCostAndCharges = useRef(null);
  const typicalAssetAllocation = useRef(null);
  const exPoliciesRiskScore = useRef(null);
  const recPoliciesRiskScore = useRef(null);
  const aggregateExPortfolioRiskScore = useRef(null);
  const exPoliciesPastPerformance = useRef(null);
  const assetAllocationsChart = useRef(null);
  const assetAllocationsExistingOnlyChart = useRef(null);
  const existingAnnualCostsAndCharges = useRef(null);
  const combinedAnnualCostsAndCharges = useRef(null);
  const existingRiskScatterChart = useRef(null);
  const recommendedRiskScatterChart = useRef(null);
  const existingPolicyRiskScoreFiltered = portfolioRiskScore.filter(
    (policyType: any) => policyType.name === 'Existing'
  );

  const isExistingPoliciesAdded = existingPolicies.length > 0;
  const areAggregatePastPerformanceDataAdded =
    recommendedPolicies.length > 0 || existingPolicies.length > 0;

  const maxChargeValue = findMaxCharge(
    existingPoliciesCharges,
    recommendedPoliciesCharges
  );

  const modifiedData = combinePolicyRiskScores(
    existingPoliciesRiskScore,
    recommendedPoliciesRiskScore
  );

  const allCharges = combineCharges(
    existingPoliciesCharges,
    recommendedPoliciesCharges
  );

  const comfortRanges = getComfortRanges(
    caseInfo.riskProfiler,
    comfortRangeConfig,
    [recommendedRiskAnalysis['comfort_ranges']]
  );

  const breakdownCharts = [
    {
      name: 'sector',
      data: mapSectorData(sectorBreakdown).map((allocation: any) => ({
        ...allocation
      }))
    },
    {
      name: 'regional_equity',
      data: regionalBreakdown.equity
        .sort((a: any, b: any) => b.Existing - a.Existing)
        .filter((allocation: any) => allocation.Existing !== 0)
    },
    {
      name: 'regional_fixed_income',
      data: regionalBreakdown.fixed_income
        .sort((a: any, b: any) => b.Existing - a.Existing)
        .filter((allocation: any) => allocation.Existing !== 0)
    },
    {
      name: 'country_equity',
      data: countryBreakdown.equity
        .sort((a: any, b: any) => b.Existing - a.Existing)
        .filter((allocation: any) => allocation.Existing !== 0)
    },
    {
      name: 'country_fixed_income',
      data: countryBreakdown.fixed_income
        .sort((a: any, b: any) => b.Existing - a.Existing)
        .filter((allocation: any) => allocation.Existing !== 0)
    },
    {
      name: 'regional_combined',
      data: mapCombinedBreakdownData({
        equity_regional_breakdown: regionalBreakdown.equity
          .sort((a: any, b: any) => b.Existing - a.Existing)
          .filter((allocation: any) => allocation.Existing !== 0),
        fixed_income_regional_breakdown: regionalBreakdown.fixed_income
          .sort((a: any, b: any) => b.Existing - a.Existing)
          .filter((allocation: any) => allocation.Existing !== 0)
      })
    },
    {
      name: 'country_combined',
      data: mapCombinedBreakdownData({
        equity_regional_breakdown: countryBreakdown.equity
          .sort((a: any, b: any) => b.Existing - a.Existing)
          .filter((allocation: any) => allocation.Existing !== 0),
        fixed_income_regional_breakdown: countryBreakdown.fixed_income
          .sort((a: any, b: any) => b.Existing - a.Existing)
          .filter((allocation: any) => allocation.Existing !== 0)
      })
    }
  ].flatMap(chart => [
    { name: chart.name, data: chart.data, existing: false },
    { name: `${chart.name}_existing`, data: chart.data, existing: true }
  ]);

  async function generateImages(charts: any) {
    const images: any = {
      charges_images: [],
      breakdown_images: {}
    };

    for (let i = 0; i < charts.length; i++) {
      const htmlElem = charts[i].chart;
      const chartName = charts[i].name;

      const isAggregatesChars = chartName === 'charges_images';
      const isBreakdownChars = chartName === 'breakdown_images';

      if (isBreakdownChars) {
        const breakdownNodeImg = charts[i].images;

        for (let j = 0; j < breakdownCharts.length; j++) {
          const elem = breakdownNodeImg[j];

          await generateImage(elem).then((imgData: any) => {
            const key = breakdownCharts[j].name; // Assign the key value
            images.breakdown_images[key] = imgData;
          });
        }
      }

      if (isAggregatesChars) {
        const aggregateNodeImg = charts[i].images;

        for (let j = 0; j < aggregateNodeImg.length; j++) {
          const elem = aggregateNodeImg[j];

          await generateImage(elem).then((imgData: any) => {
            images.charges_images.push({
              policy_id: Number(elem.id),
              image: imgData
            });
          });
        }
      }
      // @ts-ignore
      if (htmlElem) {
        await generateImage(htmlElem).then((imgData: any) => {
          images[chartName] = imgData;
        });
      }
    }

    return api
      .generatePdf({
        portfolio,
        images: { ...images, ...reportImages },
        type: location.state.downloadType,
        upload_to_io: location.state.upload_to_io,
        portfolioType: location.state.portfolioType,
        report_sections: location.state.report_sections,
        scatterChartData: {
          existing: scatterChartDataExisting,
          recommended: scatterChartDataRecommended
        },
        existingPerformanceData: getAllPerformanceData(
          existingPolicyPastPerformance.tabular_data
        ),
        recommendedPerformanceData: getAllPerformanceData(
          pastPerformanceAggregate.tabular_data
        )
      })
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        setPdf(url);
        setIsLoadingPdf(false);
        const link = document.createElement('a');
        link.href = url;
        const file_name =
          location.state.downloadType === 'pdf'
            ? `${caseName}.pdf`
            : `${caseName}.docx`;
        link.setAttribute('download', file_name);
        document.body.appendChild(link);
        link.click();
        if (location.state.portfolioType === 'existing') {
          Notify.success('Policy Analysis', `Download completed`);
          history.push(`/${portfolio}/existing-policies/policy-analysis`);
        }
      })
      .catch((err: any) => {
        if (location.state.portfolioType === 'existing') {
          Notify.error('Policy Analysis', `Download failed`);
          history.push(`/${portfolio}/existing-policies/policy-analysis`);
        }
        const reader = new FileReader();

        reader.addEventListener('loadend', ({ target }) => {
          if (target && target.result) {
            const properErrMessage = JSON.parse(target.result.toString());

            if (properErrMessage) {
              Notify.error(properErrMessage.message);
            }
          }
        });

        const errData = err.response.data;

        reader.readAsText(errData);

        setIsLoadingPdf(false);
        errHelper(err);
      });
  }

  const downloadAgain = () => {
    const link = document.createElement('a');
    link.href = pdf || '';
    const file_name =
      location.state.downloadType === 'pdf'
        ? `${caseName}.pdf`
        : `${caseName}.docx`;
    link.setAttribute('download', file_name);
    document.body.appendChild(link);
    link.click();
  };

  const getAllData = () => {
    const reportSections = location.state.report_sections;

    Promise.all([
      dispatch(getRiskProfiles(portfolio) as any),
      dispatch(getPolicies(portfolio) as any),
      dispatch(getExistingPolicies(portfolio) as any),
      dispatch(
        fetchDataIfNeeded('assetAllocations', () =>
          api.getAssetAllocationData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('portfolioRiskScore', () =>
          api.getPortfolioRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesCharges', () =>
          api.getExsitinPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesCharges', () =>
          api.getRecommendedPoliciesChargesData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('existingPoliciesRiskScore', () =>
          api.getExistingPoliciesRiskScore({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('recommendedPoliciesRiskScore', () =>
          api.getRecommendedPoliciesRiskScore({ portfolio })
        ) as any
      ),
      ...(isEvalue
        ? [
            dispatch(
              fetchDataIfNeeded('portfolioSplit', () =>
                api.getPortfolioSplitData({ portfolio })
              ) as any
            )
          ]
        : []),
      dispatch(
        fetchDataIfNeeded('sectorBreakdown', () =>
          api.getSectorBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('regionlBreakdown', () =>
          api.getRegionalBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(
        fetchDataIfNeeded('countryBreakdown', () =>
          api.getCountryBreakdownData({ portfolio })
        ) as any
      ),
      dispatch(fetchDataIfNeeded('providers', () => api.getProviders()) as any),
      dispatch(
        fetchDataIfNeeded('holdings', () =>
          api.getHoldings({ portfolio })
        ) as any
      )
      // eslint-disable-next-line
    ])
      .then(([riskSummaries, policiesResponse]) => {
        const riskSummary = riskSummaries.results[0];
        const { time_horizon_interval, recommended_risk_profile } = riskSummary;
        setRiskProfile(riskSummary);

        axios
          .all([
            api.getPastPerformanceAggregateData({ portfolio }),
            api.getPastPerformanceData({ portfolio })
          ])
          .then(
            axios.spread(
              (pastPerformanceAggregate, pastPerformanceExisting) => {
                dispatch(
                  setPastPerformanceAggregates(pastPerformanceAggregate.data)
                );
                dispatch(setPastPerformance(pastPerformanceExisting.data));
              }
            )
          )
          .catch(() => {
            Notify.error(
              'Failed to fetch past performance data',
              'Please contact support@fintegrate.co.uk if the issue persists'
            );
          });

        if (isEvalue) {
          api
            .getTypicalAssetAllocationEvalue({
              portfolio,
              timeHorizon: time_horizon_interval,
              recommendedRiskProfile: recommended_risk_profile
            })
            .then(({ data }) => {
              dispatch(setAssetAllocationEvalue(data));
            });
        }

        axios
          .all([
            api.getExistingPoliciesPerformanceData({ portfolio }),
            api.getTotalOngoingCharges({ portfolio }),
            api.getTotalPerformanceData({ portfolio })
          ])
          .then(
            axios.spread(
              (
                existingPoliciesPerformance,
                totalOngoingCharges,
                totalPerformanceData
              ) => {
                dispatch(
                  setExistingPoliciesPerformance(
                    existingPoliciesPerformance.data
                  )
                );
                dispatch(setTotalOngoingCharges(totalOngoingCharges.data));
                dispatch(setTotalPerformanceData(totalPerformanceData.data));

                const policies = policiesResponse?.data
                  ? policiesResponse.data.results
                  : policiesResponse.results;

                const policyIds = policies.map((policy: any) => policy.id);
                const costsAndChargesRequests = [];
                const costsAndCharges = [] as any;

                if (reportSections.includes('Individual policies')) {
                  policyIds.forEach((policyId: any) => {
                    const requestPromise = dispatch(
                      fetchDataIfNeeded(
                        `allAggregateCostAndCharges.${policyId}`,
                        () =>
                          api.getAggregateCostAndCharges({
                            portfolio,
                            policyId
                          })
                      ) as any
                    ).then((response: any) => {
                      const data = response?.data || response;
                      costsAndCharges.push({
                        policyId,
                        data: data.chart
                      });
                    });

                    costsAndChargesRequests.push(requestPromise);
                  });
                }

                if (reportSections.includes('Consolidated portfolio')) {
                  const policyId = null;
                  const requestPromise = dispatch(
                    fetchDataIfNeeded(
                      `allAggregateCostAndCharges.${policyId}`,
                      () =>
                        api.getAggregateCostAndCharges({ portfolio, policyId })
                    ) as any
                  ).then((response: any) => {
                    const data = response?.data || response;
                    costsAndCharges.push({
                      policyId,
                      data: data.chart
                    });
                  });

                  costsAndChargesRequests.push(requestPromise);
                }

                Promise.all(costsAndChargesRequests).then(() => {
                  dispatch(setAggregateDataForPolicies(costsAndCharges));
                  setTimeout(() => {
                    // WAIT FOR ANIMATION IN CHARTS;
                    setIsDataLoaded(true);
                  }, 1000);
                });
              }
            )
          );
      })
      .catch(() => {
        setReportFailed(true);
        Notify.error(
          'Failed to generate report',
          'Please contact support@fintegrate.co.uk if the issue persists'
        );
      });
  };

  useEffect(() => {
    if (!isDataLoaded) {
      getAllData();
    } else {
      const allAggregates = document.querySelectorAll(
        'div[data-ref="aggregateCostCharges"'
      );

      const allBreakdownCharts = document.querySelectorAll(
        'div[data-ref="breakdownData"'
      );

      const charts = [
        {
          chart: aggregatePastPerformance.current,
          name: 's_recommended_portfolio_p10_performance_00'
        },
        {
          chart: existingPortfolioCharges.current,
          name: 's_existing_portfolio_p06_charges_00'
        },
        {
          chart: recommendedPortfolioCharges.current,
          name: 's_recommended_portfolio_p10_charges_00'
        },
        {
          chart: forecastInvestmentReturn.current,
          name: 's_asset_p03_forecast_00'
        },
        {
          chart: aggregatePortfolioRisk.current,
          name: 's_recommended_portfolio_p09_risk_analysis_00'
        },
        {
          chart: recommendedPortfolioSplit.current,
          name: 's_recommended_portfolio_p09_split_00'
        },

        { chart: riskSummary.current, name: 's_risk_p01_summary_00' },
        {
          chart: typicalAssetAllocation.current,
          name: 's_asset_p03_allocation_00'
        },
        {
          chart: exPoliciesRiskScore.current,
          name: 's_existing_portfolio_p06_risk_analysis_00'
        },
        {
          chart: recPoliciesRiskScore.current,
          name: 's_recommended_portfolio_p06_risk_analysis_00'
        },
        {
          chart: aggregateExPortfolioRiskScore.current,
          name: 's_existing_portfolio_p06_risk_analysis_01'
        },
        {
          chart: exPoliciesPastPerformance.current,
          name: 's_existing_portfolio_p06_past_performance_00'
        },
        {
          chart: totalCostAndCharges.current,
          name: 's_recommended_portfolio_p10_risk_analysis_00'
        },
        {
          chart: assetAllocationsChart.current,
          name: 's_recommended_portfolio_p09_allocation_00'
        },
        {
          chart: assetAllocationsExistingOnlyChart.current,
          name: 's_existing_portfolio_p09_allocation_00'
        },
        {
          chart: existingAnnualCostsAndCharges.current,
          name: 's_existing_annual_costs_and_charges'
        },
        {
          chart: combinedAnnualCostsAndCharges.current,
          name: 's_combined_annual_costs_and_charges'
        },
        {
          chart: existingRiskScatterChart.current,
          name: 's_existing_risk_scatter_chart_00'
        },
        {
          chart: recommendedRiskScatterChart.current,
          name: 's_recommended_risk_scatter_chart_00'
        },
        {
          name: 'charges_images',
          images: allAggregates
        },
        {
          name: 'breakdown_images',
          images: allBreakdownCharts
        }
      ];

      generateImages(charts).then(() => {
        setIsLoadingPdf(false);
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  const documentType =
    location.state.downloadType === 'pdf' ? 'pdf' : 'Word document';

  return (
    <Grid
      container
      direction="row"
      justifyContent="flex-start"
      alignItems="flex-start"
      className={classes.root}
      spacing={2}
    >
      <div className={classes.mask}>
        {!reportFailed && (isLoadingPdf || !isDataLoaded) ? (
          <Paper className={classes.modal}>
            <h2>Preparing {documentType}...</h2>
            <CircularProgress size={20} />
          </Paper>
        ) : (
          <Paper className={classes.modal}>
            <h2>Download completed</h2>
            <div className={classes.btnContainer}>
              <Button onClick={() => history.push(`/${portfolio}/ips`)}>
                Go back to Summary
              </Button>
              <Button
                color="primary"
                disabled={pdf.length === 0}
                onClick={downloadAgain}
              >
                Download again
              </Button>
            </div>
          </Paper>
        )}
      </div>

      {/* RISK SCORE - gauge */}
      <Grid container id="page1" className={classes.print} spacing={2}>
        {isEvalue && (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={6}
            className={classes.smallBox}
            style={{ height: '400px' }}
          >
            <Paper className={classes.gridBox} style={{ height: '400px' }}>
              <div
                style={{
                  flex: '1',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 1,
                  position: 'relative',
                  // top: '-50rem',
                  marginBottom: '-22px'
                }}
                ref={aggregatePortfolioRisk}
              >
                <Charts.IPS.RiskScore
                  data={portfolioRiskScore}
                  hasExistingPolicies={hasExistingPolicies}
                  riskGroup={firmRiskGroup}
                  footer="Excludes any allocation to Wrap cash, where applicable."
                />
              </div>
            </Paper>
          </Grid>
        )}

        {/* RISK Summary- */}
        <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
          <Paper className={classes.gridBox}>
            <Grid item xs={12} className={classes.chartBox} ref={riskSummary}>
              <Charts.IPS.RiskSummary
                data={riskSummaries}
                isAnimationActive={false}
                height={250}
              />
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
          <Paper className={classes.gridBox}>
            <Grid
              item
              xs={12}
              className={classes.chartBox}
              ref={forecastInvestmentReturn}
            >
              <Charts.IPS.ForecastGrossInvReturns
                isAnimationActive={false}
                data={forecastGrossInvestmentReturn}
              />
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Portfolio split  */}
      {isEvalue && (
        <Grid item xs={12} className={classes.semiMiddleBox}>
          <Paper style={{ height: '100%' }}>
            <Grid
              item
              xs={12}
              className={classes.chartBox}
              ref={recommendedPortfolioSplit}
            >
              <Charts.IPS.PortfolioSplit
                isAnimationActive={false}
                data={portfolioSplit}
                bigLabels
                height={500}
              />
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Past performance  */}
      <Grid item xs={12}>
        <Paper>
          <Grid item xs={12} ref={aggregatePastPerformance}>
            {pastPerformanceAggregate.data && (
              <PastPerformance
                isAnimationActive={false}
                data={pastPerformanceAggregate.data}
                errors={pastPerformanceAggregate.failed_codes}
                limits={pastPerformanceAggregate.limits}
                earliestDates={pastPerformanceAggregate.earliest_dates}
                footprints={pastPerformanceAggregate.footprints}
                height={700}
                bigLabels
                isAnyPolicyAdded={areAggregatePastPerformanceDataAdded}
                isForPdf={true}
                hasDateControl={false}
              />
            )}
            <span style={{ fontSize: '16px' }}>
              {pastPerformanceAggregate.date_range}
            </span>
          </Grid>
        </Paper>
      </Grid>

      {/* ex policy charges  */}

      <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
        <Paper className={classes.gridBox}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={existingPortfolioCharges}
          >
            <Charts.IPS.PolicyCharges
              maxChargeValue={maxChargeValue}
              data={existingPoliciesCharges}
              isAnimationActive={false}
              height={250}
            />
          </Grid>
        </Paper>
      </Grid>
      {/* recommended policy charges  */}
      <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
        <Paper className={classes.gridBox}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={recommendedPortfolioCharges}
          >
            <Charts.IPS.PolicyCharges
              maxChargeValue={maxChargeValue}
              data={recommendedPoliciesCharges}
              isAnimationActive={false}
              height={200}
            />
            <p className={classes.tooltipCharge}>
              - Excluding any initial and/or ongoing adviser charges.
            </p>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} className={classes.largeBox}>
        <Paper>
          <Grid item xs={12} ref={existingAnnualCostsAndCharges}>
            <Charts.ExistingPolicyAnalysis.PolicyCharges
              data={existingPoliciesCharges}
            />
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Grid item xs={12} ref={combinedAnnualCostsAndCharges}>
            <Charts.ExistingPolicyAnalysis.PolicyCharges data={allCharges} />
          </Grid>
        </Paper>
      </Grid>

      {/* DONUT */}
      <Grid item xs={12} sm={8} md={8} lg={8} className={classes.middleBox}>
        <Paper className={classes.gridBox}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={typicalAssetAllocation}
          >
            <TypicalAssetAllocations
              data={assetAllocationEvalue}
              isAnimationActive={false}
            />
          </Grid>
        </Paper>
      </Grid>
      {/* ex policies risk score */}
      <Grid item xs={12} className={classes.largeBox}>
        <Paper style={{ height: '100%' }}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={exPoliciesRiskScore}
          >
            <Charts.ExistingPolicyAnalysis.PoliciesRiskScore
              data={modifiedData}
              isAnimationActive={false}
              riskGroup={firmRiskGroup}
              existingOnly={true}
              footer="Excludes any allocation to Wrap cash, where applicable."
            />
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} className={classes.largeBox}>
        <Paper style={{ height: '100%' }}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={recPoliciesRiskScore}
          >
            <Charts.ExistingPolicyAnalysis.PoliciesRiskScore
              data={modifiedData}
              isAnimationActive={false}
              riskGroup={firmRiskGroup}
              footer="Excludes any allocation to Wrap cash, where applicable."
            />
          </Grid>
        </Paper>
      </Grid>

      {/* ex policies Gauge  */}
      {isEvalue && (
        <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
          <Paper className={classes.gridBox} style={{ height: '400px' }}>
            <div
              style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1,
                position: 'relative',
                height: '400px'
              }}
              ref={aggregateExPortfolioRiskScore}
            >
              <Charts.ExistingPolicyAnalysis.RiskScore
                data={existingPolicyRiskScoreFiltered[0]}
                riskGroup={firmRiskGroup}
                footer="Excludes any allocation to Wrap cash, where applicable."
                animate={false}
              />
            </div>
          </Paper>
        </Grid>
      )}

      {/* ex policies past perferomance */}
      <Grid item xs={12}>
        <Paper>
          <Grid item xs={12} ref={exPoliciesPastPerformance}>
            {existingPolicyPastPerformance.data && (
              <PastPerformance
                data={existingPolicyPastPerformance.data}
                errors={existingPolicyPastPerformance.failed_codes}
                limits={existingPolicyPastPerformance.limits}
                earliestDates={existingPolicyPastPerformance.earliest_dates}
                isAnimationActive={false}
                height={700}
                bigLabels
                isAnyPolicyAdded={isExistingPoliciesAdded}
                isForPdf={true}
                hasDateControl={false}
              />
            )}
            <span>{existingPolicyPastPerformance.date_range}</span>
          </Grid>
        </Paper>
      </Grid>

      {/* recommendaation total cost & chaarges */}
      <Grid item xs={12} sm={6} md={6} lg={6} className={classes.smallBox}>
        <Paper className={classes.gridBox}>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            ref={totalCostAndCharges}
          >
            <Charts.Recommendation.TotalOngoingCharge
              data={totalOngoinCharges}
              isAnimationActive={false}
              hasExistingPolicies={hasExistingPolicies}
              height={200}
            />
            <p className={classes.tooltip}>
              - Excluding any initial and/or ongoing adviser charges.
            </p>
          </Grid>
        </Paper>
      </Grid>

      {/* recommendaation Asset allocation */}
      <Grid item xs={12} md={8}>
        <Paper>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            style={{ padding: '1rem', width: '800px' }}
            ref={assetAllocationsChart}
          >
            <Charts.Recommendation.AssetAllocation
              data={assetAllocations.filter(
                (data: any) => data.Existing !== 0 || data.Recommended !== 0
              )}
              isAnimationActive={false}
              hasExistingPolicies={hasExistingPolicies}
              bigLabels
            />
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper>
          <Grid
            item
            xs={12}
            className={classes.chartBox}
            style={{ padding: '1rem', width: '800px' }}
            ref={assetAllocationsExistingOnlyChart}
          >
            <Charts.Recommendation.AssetAllocation
              data={assetAllocations.filter(
                (allocation: any) => allocation.Existing !== 0
              )}
              isAnimationActive={false}
              existingOnly={true}
            />
          </Grid>
        </Paper>
      </Grid>

      {breakdownCharts.map((breakdownChart: any) => {
        return (
          <Grid
            item
            xs={12}
            sm={8}
            md={8}
            lg={8}
            key={breakdownChart.name}
            style={{ width: '600px', maxWidth: '600px' }}
          >
            <Paper
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: '1rem',
                border: '1px solid #e5dede'
              }}
            >
              <Grid
                item
                xs={12}
                className={classes.chartBox}
                data-ref="breakdownData"
              >
                {breakdownChart.name === 'regional_combined' ||
                breakdownChart.name === 'country_combined' ? (
                  <CountryBreakdownChart
                    data={breakdownChart.data}
                    isForReport
                  />
                ) : (
                  <Grid item style={{ padding: '1rem' }}>
                    <Charts.Recommendation.AssetAllocation
                      data={breakdownChart.data}
                      isAnimationActive={false}
                      hasExistingPolicies={hasExistingPolicies}
                      bigLabels
                      existingOnly={breakdownChart.existing}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        );
      })}

      <Grid item xs={12} md={8} className={classes.scatterBox}>
        <Paper>
          <Grid
            item
            xs={12}
            style={{ height: '100%' }}
            ref={existingRiskScatterChart}
          >
            {existingPoliciesPerformance.length > 0 &&
              (existingPolicyPastPerformance?.data || []).length > 0 && (
                <ScatterChart
                  key={2}
                  data={existingPoliciesPerformance}
                  performanceData={
                    existingPolicyPastPerformance?.tabular_data || {}
                  }
                  setReportData={setScatterChartDataExisting}
                  isForReport={true}
                />
              )}
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8} className={classes.scatterBox}>
        <Paper>
          <Grid
            item
            xs={12}
            style={{ height: '100%' }}
            ref={recommendedRiskScatterChart}
          >
            {totalPerformanceData.length > 0 &&
              (pastPerformanceAggregate?.data || []).length > 0 && (
                <ScatterChart
                  key={1}
                  data={totalPerformanceData}
                  performanceData={pastPerformanceAggregate?.tabular_data || {}}
                  setReportData={setScatterChartDataRecommended}
                  isForReport={true}
                />
              )}
          </Grid>
        </Paper>
      </Grid>

      {!isEvalue && (
        <>
          <MorningstarRiskToleranceReportChart
            showSuitability={true}
            showRisk={true}
          />
          {riskProfile && (
            <TypicalAssetAllocationReportChart
              data={getTargetAllocationData(
                riskProfile.recommended_risk_profile
              )}
            />
          )}
          <MorningstarRiskAnalysisReportChart
            name="morningstar_existing_risk_analysis_chart"
            existingOnly
            ranges={comfortRanges}
          />
          <MorningstarRiskAnalysisReportChart ranges={comfortRanges} />
        </>
      )}

      {aggregateCostAndChargesPolicies.map(
        (aggregateCostChargesPolicy: AggregateCostAndChargesPoliciesType) => {
          return (
            <Grid item xs={12} key={aggregateCostChargesPolicy.policyId}>
              <Paper>
                <div
                  data-ref="aggregateCostCharges"
                  id={`${aggregateCostChargesPolicy.policyId}`}
                  style={{ padding: '10px' }}
                >
                  <Grid item xs={12} className={classes.chartBox}>
                    <Charts.IPS.AggregateCostAndCharges
                      data={aggregateCostChargesPolicy.data}
                      isAnimationActive={false}
                      height={570}
                      // bigLabels
                    />
                  </Grid>
                  <div className={classes.legend}>
                    <div className={classes.legendSingle}>
                      <div
                        className={classes.legendBox}
                        style={{
                          backgroundColor:
                            theme.palette.baseColors.charts['adviser-charges'][
                              adviserChargesIndex('annualised_contributions')
                            ]
                        }}
                      ></div>
                      <span style={{ fontSize: '16px' }}>
                        Annualised contributions
                      </span>
                    </div>
                    <div className={classes.legendSingle}>
                      <div
                        className={classes.legendBox}
                        style={{
                          backgroundColor:
                            theme.palette.baseColors.charts['adviser-charges'][
                              adviserChargesIndex('annualised_withdrawals')
                            ]
                        }}
                      ></div>
                      <span style={{ fontSize: '16px' }}>
                        Annualised withdrawals
                      </span>
                    </div>
                    <div className={classes.legendSingle}>
                      <div
                        className={classes.legendBox}
                        style={{
                          backgroundColor:
                            theme.palette.baseColors.charts['adviser-charges'][
                              adviserChargesIndex('product_charges')
                            ]
                        }}
                      ></div>
                      <span style={{ fontSize: '16px' }}>
                        Cumulative ongoing investment management and
                        product/platform charges
                      </span>
                    </div>
                    <div className={classes.legendSingle}>
                      <div
                        className={classes.legendBox}
                        style={{
                          backgroundColor:
                            theme.palette.baseColors.charts['adviser-charges'][
                              adviserChargesIndex('advisor_charges')
                            ]
                        }}
                      ></div>
                      <span style={{ fontSize: '16px' }}>
                        Cumulative ongoing adviser charges
                      </span>
                    </div>

                    <div className={classes.legendSingle}>
                      <div
                        className={classes.legendBox}
                        style={{
                          backgroundColor:
                            theme.palette.baseColors.charts['adviser-charges'][
                              adviserChargesIndex('net_value')
                            ]
                        }}
                      ></div>
                      <span style={{ fontSize: '16px' }}>
                        Net value at end of period
                      </span>
                    </div>
                  </div>
                </div>
              </Paper>
            </Grid>
          );
        }
      )}
    </Grid>
  );
};

export default compose(
  connect(
    (state: any) => ({
      pastPerformanceAggregate:
        selectors.caseSelectors.pastPerformanceAggregates(state),
      portfolioSplit: selectors.caseSelectors.portfolioSplit(state),
      existingPoliciesCharges:
        selectors.caseSelectors.existingPoliciesCharges(state),
      existingPolicies: selectors.caseSelectors.existingPolicies(state),
      recommendedPolicies: selectors.caseSelectors.policies(state),
      recommendedPoliciesCharges:
        selectors.caseSelectors.recommendedPoliciesCharges(state),
      riskSummaries: selectors.IPSSelectos.riskSummary(state),
      forecastGrossInvestmentReturn:
        selectors.IPSSelectos.forecastGrossInvestmentReturn(state),
      portfolioRiskScore: selectors.caseSelectors.portfolioRiskScore(state),
      caseName: selectors.IPSSelectos.caseName(state),
      existingPoliciesRiskScore:
        selectors.caseSelectors.existingPoliciesRiskScore(state),
      recommendedPoliciesRiskScore:
        selectors.caseSelectors.recommendedPoliciesRiskScore(state),
      existingPolicyPastPerformance:
        selectors.caseSelectors.pastPerformance(state),
      existingPoliciesPerformance:
        selectors.caseSelectors.existingPoliciesPerformance(state),
      totalPerformanceData: selectors.caseSelectors.totalPerformanceData(state),
      totalOngoinCharges: selectors.caseSelectors.totalOngoingCharges(state),
      assetAllocations: selectors.caseSelectors.assetAllocations(state),
      countryBreakdown: selectors.caseSelectors.countryBreakdown(state),
      regionalBreakdown: selectors.caseSelectors.regionlBreakdown(state),
      sectorBreakdown: selectors.caseSelectors.sectorBreakdown(state),
      policies: selectors.caseSelectors.policies(state),
      aggregateCostAndChargesPolicies:
        selectors.caseSelectors.aggregateCostAndChargesPolicies(state),
      hasExistingPolicies: selectors.caseSelectors.hasExistingPolicies(state),
      firmRiskGroup: selectors.firmSelectors.riskGroup(state),
      assetAllocationEvalue:
        selectors.caseSelectors.assetAllocationEvalue(state),
      caseInfo: selectors.caseSelectors.case(state),
      reportImages: selectors.reportSelectors.getImages(state),
      recommendedRiskAnalysis:
        selectors.caseSelectors.recommendedRiskAnalysis(state)
    }),
    {
      setAggregateDataForPolicies:
        actions.caseActions.setAggregateDataForPolicies,
      setExistingPoliciesPerformance:
        actions.caseActions.setExistingPoliciesPerformance,
      setPastPerformanceAggregates:
        actions.caseActions.setPastPerformanceAggregates,
      setPastPerformance: actions.caseActions.setPastPerformance,
      setTotalOngoingCharges: actions.caseActions.setTotalOngoingCharges,
      setTotalPerformanceData: actions.caseActions.setTotalPerformanceData,
      setAssetAllocationEvalue: actions.caseActions.setAssetAllocationEvalue
    }
  ),
  withMeta,
  withCaseInfo
)(Printer);
